import React, { useEffect, useState } from 'react'
import { useWebSocket } from '../contexts/WebSocketContext'
import { WebSocketStatus } from '../types/websocket.types'
import { useAuth } from 'react-oidc-context'

// Configuration for WebSocket connection
const WS_CONFIG = {
  url: (() => {
    const host = (window._env_?.REACT_APP_WEBSOCKET_HOST as string) ?? ''
    return `${host}/udp/tiq/ws/dashboard`
  })(),
  tenantId: '', // Default tenant ID
  username: '' // Default username
}

interface WebSocketConnectionManagerProps {
  children: React.ReactNode
}

const WebSocketConnectionManager: React.FC<WebSocketConnectionManagerProps> = ({ children }) => {
  const { connect, status } = useWebSocket()
  const [connectionAttempted, setConnectionAttempted] = useState(false)
  const [managerRetryCount, setManagerRetryCount] = useState(0)
  const [maxManagerRetriesReached, setMaxManagerRetriesReached] = useState(false)
  const auth = useAuth()

  const MAX_MANAGER_RETRIES = 3 // Limit WebSocketConnectionManager to 3 retry cycles

  // Establish WebSocket connection when component mounts
  useEffect(() => {
    if (!connectionAttempted && auth.isAuthenticated && !maxManagerRetriesReached) {
      connect(WS_CONFIG.url, WS_CONFIG.tenantId, WS_CONFIG.username)
      setConnectionAttempted(true)
      setManagerRetryCount(prev => prev + 1)
    }
  }, [connect, connectionAttempted, auth.isAuthenticated, maxManagerRetriesReached])

  // Retry logic for WebSocket connection
  useEffect(() => {
    let retryTimeout: NodeJS.Timeout

    if (status === WebSocketStatus.DISCONNECTED && connectionAttempted && !maxManagerRetriesReached) {
      retryTimeout = setTimeout(() => {
        connect(WS_CONFIG.url, WS_CONFIG.tenantId, WS_CONFIG.username)
        setManagerRetryCount(prev => prev + 1)
      }, 2000) // Retry after 2 seconds
    }

    // Check if max retries reached
    if (managerRetryCount >= MAX_MANAGER_RETRIES) {
      setMaxManagerRetriesReached(true)
    }

    return () => {
      clearTimeout(retryTimeout)
    }
  }, [status, connectionAttempted, connect, managerRetryCount, maxManagerRetriesReached])

  return <>{children}</>
}

export default WebSocketConnectionManager
